import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import { Empty } from "./Empty";
import { OcAxisHelper } from "oc-threejs/objects/OcAxisHelper";
import * as Commons from "oc-commons/Commons";
import * as Maths from "oc-commons/Maths";
import { Pfosten } from "./Pfosten";
import { Ecke } from "./Ecke";
import { createClass } from "./createClass";
import { v2, v3 } from "oc-threejs/short3";
import * as Maths3 from "oc-threejs/Maths3";
import * as Maths2 from "oc-threejs/Maths2";
import * as Scenes from "oc-threejs/Scenes";
import * as ClosedInterval from "oc-commons/ClosedInterval";
import { intersection, Interval } from "oc-commons/ClosedInterval";
import { UMProfil } from "./UMProfil";
import { Object3D, Ray, Vector3 } from "three";
import { JSON_DECIMALS } from "../commons/Utils";
import { getTypeNameOfPart } from "./arrangementFunctions";
import {
  calcElementAnzahl,
  calcElementAnzahlGleichverteilt,
  calcElementAnzahlStandardMitMasselementen,
  calcElementBreite,
  getIstSollZwischenabstandDelta,
  getSegmentEndenAusgleichForEcke,
  getZwischenabstand,
  getZwischenabstandVerbose,
  LINKS,
  RECHTS,
  toAbschnittsteil,
  ZwischenabstandVerbose,
} from "./elementFunctions";
import { ZElement } from "./ZElement";
import { Arrangement } from "./Arrangement";
import { I18N } from "oc-I18N";
import {
  GrundrissKante,
  pfostenbreiteCirca,
  segmentIntervallUnion,
  SegmentKante,
  v2ToAngle,
} from "./grundrissFunctions";
import _ from "lodash";
import {
  AbschnittRuler,
  computeRulerVersatz,
  RULER_BASE_VERSATZ,
  RulerStyle,
} from "./AbschnittRuler";
import { Eckadapter } from "./Eckadapter";
import {
  getPfostenWinkel,
  initInsertPfostenFunction,
  isWinkelbereichUForPfosten,
} from "./pfostenFunctions";
import { MAuswahlwert } from "../../metamodell";
import * as Dialogs from "oc-ui/dialogs/Dialogs";
import { getAngleIntervalType } from "../commons/AngleIntervals";
import { Platzhalter } from "./Platzhalter";
import { BoxRuler } from "../arrangement3d/BoxRuler";
import * as Debugs from "oc-threejs/Debugs";
import { StücklistenIterator } from "./Stückliste";
import { LineBasicMaterial } from "three";

export type VerteilungsstrategieAuswahlwertKey =
  | "Individuell"
  | "Gleichmässig"
  | "StandardLinks"
  | "StandardRechts"
  | "LeerraumLinks"
  | "LeerraumRechts"
  | "KeinZaun";

export class Segment extends Jaco {
  static CLASS_NAME = "Segment";

  compareX = (a, b) => this.getX(a, 0.5) - this.getX(b, 0.5);

  isSegment = true;

  // ----------------------------------------------------------------------------------
  // persistierte Werte - Zustand, werden mit der Konfiguration gespeichert
  // ----------------------------------------------------------------------------------
  //   breite: Maths.round(this.breite, JSON_DECIMALS),
  //   verteilungsstrategieAuswahlwert: this.verteilungsstrategieAuswahlwert_?.Key,

  parts: any[] = [];
  // Pfosten können UMProfile sein
  pfosten: Pfosten[] = [];
  // hier sind nur die UMProfile drin, die keine Pfosten sind
  umprofile: UMProfil[] = [];
  breite = 0;
  verteilungsstrategieAuswahlwert_: MAuswahlwert;

  // ----------------------------------------------------------------------------------
  // abgeleitete Werte - d.h. sind letztlich nur berechnet (in update3d) und gecached, kein Zustand
  // Achtung, diese Werte muss man immer ganz besonders im Auge haben, kann sein,
  // dass sie fälschlich doch als Zustand dienen, und das führt dann zu schwierigen Bugs
  // ----------------------------------------------------------------------------------

  segmentKante: SegmentKante;
  // es gibt immer 2 Ecken, und anliegende Segmente teilen sich gemeinsame Ecken
  ecken: [Ecke, Ecke] | [] = [];
  eckadapter: Eckadapter[] = [];
  // die Direction ist im Uhrzeigersinn
  direction = v3(1, 0, 0);
  // die normal zeigt aus dem Grundstück heraus - ausser bei Form Strecke
  normal = v3(0, 0, 1);
  flippedNormal = false;
  // eckadapterDelta[0] entspricht der linken Ecke, eckadapterDelta[1] der rechten
  // der durch Eckadapter bedingte Versatz vom Pfosten (halber Pfostenbreite) bis zum Rand des Eckadapters
  // entweder 0 (kein Eckadapter) oder > 0
  eckadapterDelta = [0, 0];
  // gibt die absolute Himmelsrichtung an. Aber Achtung! Ist von Ecke 0 ausgehend. Um den Winkel
  // bezogen auf eine Ecke zu ermitteln, muss die Methode getAngleFromEcke benutzt werden.
  angle = 0;
  partsBackup: any[] = [];
  empties: Empty[] = [];
  platzhalter: Platzhalter[] = [];
  ruler: AbschnittRuler[] = [];
  massRuler: BoxRuler = null;
  debugLine: Object3D;
  updateCounter = 0;
  _debugTable: any;

  get kanteId() {
    return this.segmentKante.grundrissKante.id;
  }

  get arrangement(): Arrangement {
    return super.arrangement as Arrangement;
  }

  get verteilungsstrategieAuswahlwert(): MAuswahlwert {
    return (
      this.verteilungsstrategieAuswahlwert_ ?? this.arrangement.verteilungsstrategieAuswahlwert_
    );
  }

  set verteilungsstrategieAuswahlwert(v: MAuswahlwert) {
    this.verteilungsstrategieAuswahlwert_ = v;
  }

  isVerteilungsstrategieKeinZaun() {
    return this.verteilungsstrategieAuswahlwert_?.Key == "KeinZaun";
  }

  constructor(parent: Arrangement) {
    super(parent);
  }

  traverse(iterator: (jaco: Jaco) => void) {
    super.traverse(iterator);
    if (this.parts) this.parts.forEach((o) => o.traverse(iterator));
    if (this.empties) this.empties.forEach((o) => o.traverse(iterator));
    if (this.platzhalter) this.platzhalter.forEach((o) => o.traverse(iterator));
    if (this.pfosten)
      this.pfosten.forEach((o) => {
        if (!o.isPfostenAnEcke) o.traverse(iterator);
      });
    if (this.umprofile) this.umprofile.forEach((o) => o.traverse(iterator));
    if (this.ruler) this.ruler.forEach((o) => o.traverse(iterator));
  }

  update3d() {
    if (this.updateCounter++ > 0) {
      console.error(
        "segment.update3d sollte immer nur genau ein mal in Kontext von arrangement.update3d aufgerufen werden, weil pfosten.elemente=[] im Arrangement passiert und ansonsten mehrfache Einträge in pfosten.elemente entstehen"
      );
      debugger;
    }
    super.update3d();
    this.updateCoordinateSystem();
    this.jaco3d.name = "Segment";
    if (APP.debugMode) {
      Scenes.remove(this.debugLine);
      this.debugLine = Scenes.line([v3(), v3(this.breite, 0, 0)], {
        color: 0x00ff00,
        linewidth: 1,
      });
    }
  }

  get segmentIndex(): number {
    return this.arrangement.segmente.indexOf(this);
  }

  updateParts() {
    const strategie = this.verteilungsstrategieAuswahlwert
      ?.Key as VerteilungsstrategieAuswahlwertKey;
    if (strategie === "Individuell") {
      this.getParts().forEach((p) => p.update3d());
      this.repositionParts(this.getPartsAndPfostenWithoutEmpties());
    } else {
      this.updateVerteilungsstrategie(strategie);
    }

    this.insertPfosten();
    this.insertUMProfile();

    const nonAutoEmpties = () => this.getPartsAndPfosten().filter((x) => !x.automatischeBreite);
    this.setNachbarn();
    this.calculateGaps(nonAutoEmpties());
    nonAutoEmpties().forEach((part) => {
      part.update3d();
      if (part instanceof Pfosten) {
        if (part.isUMProfil || !part.isPfostenAnEcke) {
          part.parent = this;
        } else {
          part.parent = this.arrangement;
        }
      }
    });
    this.repositionParts(nonAutoEmpties());
    this.setNachbarn();
    // das ist aufgrund des von der exakten Positionierung abhängigen UMProfil/Pfosten-Wechsels notwendig..
    const pfosten = this.getPfosten();
    // die Eckpfosten werden später in Arrangement.js geupdated
    pfosten
      .filter((p) => !p.isPfostenAnEcke)
      .forEach((p) => {
        p.update3d();
        p.update3dModel();
      });
    if (this.removeCollidingPfosten()) {
      this.repositionParts(nonAutoEmpties());
      this.setNachbarn();
    }
    this.updateEmpties();
    this.setNachbarn();
    if (this.removeNonAutoEmptiesNextToEmpties()) {
      this.updateEmpties();
      this.setNachbarn();
    }
    this.checkAndMaybeUpdateSnaps();
    this.maybeFitToreZwischenMauern();
  }

  updateVerteilungsstrategie(strategie: VerteilungsstrategieAuswahlwertKey) {
    // console.log("##############################################");
    // console.log(`updateVerteilungsstrategie(${strategie})`);
    // this.logit();
    const vorgabeElement = this.arrangement.createDefaultElementWithout3D();
    vorgabeElement.parent = this;
    if (!vorgabeElement.isBreiteVariabel) {
      if (strategie === "Gleichmässig" || strategie === "StandardLinks") {
        strategie = "LeerraumLinks";
      } else if (strategie === "StandardRechts") {
        strategie = "LeerraumRechts";
      }
    }
    const tolerance = 0.005; // ±5 mm
    const epsilon = pfostenbreiteCirca;
    const elementMinBreite = this.arrangement.getElementMinBreite();
    const standardBreite = vorgabeElement.meta.Mass.Standard.x;
    const minBreite = vorgabeElement.sizeRange.box.min.x;
    const maxBreite = vorgabeElement.sizeRange.box.max.x;

    const tore = this.getTore();
    tore.forEach((p) => (p.snap = 0));
    const getNextTorForX = (x: number) =>
      _.minBy(torIntervalle, (torInterval: [number, number, ZElement]) =>
        ClosedInterval.distance(x, torInterval as any as Interval)
      )?.[2];
    const getEckenIndexForX = (x: number) =>
      Math.abs(x) < epsilon ? 0 : Math.abs(this.breite - x) < epsilon ? 1 : null;
    const getEckeForX = (x: number) => {
      const i = getEckenIndexForX(x);
      return i == null ? null : this.ecken[i];
    };
    const getAbschnittsendeForX = (x: number, element: ZElement) => {
      const ecke = getEckeForX(x);
      if (ecke) {
        const pfosten = ecke.pfosten;
        if (pfosten) {
          const angle = getPfostenWinkel(pfosten, element).angle;
          const intervalType = getAngleIntervalType(pfosten, angle);
          const isWinkelbereichU = intervalType === "U";
          const isEckadapter = intervalType === "E";
          // FIXME: Henne-Ei-Problem: es kann sein, dass durch das anliegende Element ein anderes
          //  pfosten.meta notwendig würde (divergenter, alternierender testEckeInnen2).
          //  getAbschnittsendeForX ist also gar nicht so passiv. Man müsste wissen, ob ein Element
          //  anliegen wird. Falls ja, dann muss man den Pfosten entsprechend den anliegenden Elemnten
          //  wählen, und diesen zurückgeben. Allerdings kann man an dieser Stelle noch nicht wissen,
          //  ob ein Element oder Leerraum anliegen werden wird, weil das davon abhängen kann, welcher Pfosten
          //  verwendet wird => nicht einfach auflösbarer Zyklus
          return toAbschnittsteil(pfosten.meta, {
            isWinkelbereichU,
            isEckadapter,
            isSegmentende: true,
          });
        }
        return toAbschnittsteil(ecke);
      }
      return toAbschnittsteil(getNextTorForX(x));
    };
    const getTorInterval = (tor: ZElement): [number, number, ZElement] => [
      this.getX(tor, 0),
      this.getX(tor, 1),
      tor,
    ];

    // Sorge dafür, dass Tore die Mindestabstände zum Rand des Segmentes beachten.
    // Falls nicht, dann snappen sie an die mögliche Randposition.
    const getEndAbstand = (x: number, element: ZElement) => {
      const abschnittsende = getAbschnittsendeForX(x, element);
      const endAbstand = getZwischenabstandVerbose(
        abschnittsende,
        toAbschnittsteil(element),
        this.arrangement
      );
      return endAbstand;
    };
    for (const tor of tore) {
      const [x0, x1] = getTorInterval(tor);
      if (x0 < elementMinBreite) {
        const endAbstand = getEndAbstand(0, tor).zwischenabstand;
        this.setX(tor, 0, endAbstand);
      } else if (x1 > this.breite - elementMinBreite) {
        const endAbstand = getEndAbstand(this.breite, tor).zwischenabstand;
        this.setX(tor, 1, this.breite - endAbstand);
      }
    }

    const torIntervalle = tore.map(getTorInterval);
    const verteilungsabschnitte = segmentIntervallUnion(
      this.breite,
      torIntervalle as any as Interval[],
      true,
      elementMinBreite
    );
    const nichtTore = this.parts.filter((p: any) => !p.isTor);

    const verteileElemente = (
      starteLinks: boolean,
      getElementAnzahl: (elementAbstand: ZwischenabstandVerbose, sollBreite: number) => number,
      getElementBreite: (i: number) => number
    ) => {
      const vorgabeElementAbschnittsteil = toAbschnittsteil(vorgabeElement, {
        isAufhängungAnLinkerTorseite: false,
        isWinkelbereichU: false,
      });
      const allReusableParts = [].concat(nichtTore).concat(this.pfosten).concat(this.umprofile);
      // if (starteLinks) {
      //   allReusableParts.reverse();
      // }
      const vorgabeJson = vorgabeElement.toJSON();
      this.parts = tore;
      this.pfosten = [];
      verteilungsabschnitte.forEach(([abschnittsStartX, abschnittsEndX]) => {
        // Bestimme wiederverwendbare Teile im aktuellen Abschnitt
        const abschnittsParts = allReusableParts.filter((part) => {
          const partX = this.getX(part, 0.5);
          return partX >= abschnittsStartX && partX <= abschnittsEndX;
        });
        const reusePartsMap = Commons.groupBy(abschnittsParts, (p) => Commons.typeName(p));

        const endAbstände = [
          getEndAbstand(abschnittsStartX, vorgabeElement).zwischenabstand,
          getEndAbstand(abschnittsEndX, vorgabeElement).zwischenabstand,
        ];
        const endDelta = _.sum(endAbstände);
        const elementAbstand = getZwischenabstandVerbose(
          vorgabeElementAbschnittsteil,
          vorgabeElementAbschnittsteil,
          this.arrangement
        );
        const abschnittsBreite = abschnittsEndX - abschnittsStartX;
        const sollBreite = abschnittsBreite - endDelta;
        const snap = starteLinks ? -1 : 1;
        let currentX = starteLinks
          ? abschnittsStartX + endAbstände[0]
          : abschnittsEndX - endAbstände[1];
        const direction = starteLinks ? 1 : -1;
        const side = starteLinks ? 0 : 1;
        const elementAnzahl = getElementAnzahl(elementAbstand, sollBreite);
        const directionalPop = starteLinks ? (list) => list?.shift() : (list) => list?.pop();

        for (let i = 0; i < elementAnzahl; i++) {
          const elementBreite = getElementBreite(i);
          // Versuche, ein wiederverwendbares Element zu entnehmen und prüfe, ob dessen Breite innerhalb der Toleranz liegt
          let candidate = directionalPop(reusePartsMap.get("Element")) as ZElement;
          const candidateBefore = candidate;
          // if (candidate && Math.abs(candidate.breite - elementBreite) > tolerance) {
          //   candidate = null;
          // }
          const element = candidate ?? createClass("Element", this).fromJSON(vorgabeJson);
          this.addPart(element);
          element.updateMetas();
          element.updateSize();
          element.snap = snap;
          element.setBreite(elementBreite);
          this.setX(element, side, currentX);
          currentX += direction * (element.breite + elementAbstand.zwischenabstand / 2);

          const pfosten =
            (directionalPop(reusePartsMap.get("Pfosten")) as Pfosten) ??
            createClass("Pfosten", this);
          this.pfosten.push(pfosten);
          pfosten.snap = snap;
          this.setX(pfosten, 0.5, currentX);
          currentX += direction * (elementAbstand.zwischenabstand / 2);
        }
      });
    };

    if (strategie === "Gleichmässig") {
      let idealeBreite = 0;
      verteileElemente(
        true,
        (elementAbstand, sollBreite) => {
          const zwischenabstand = elementAbstand.zwischenabstand;
          const { anzahlElemente, elementBreite, restBreite } = calcElementAnzahlGleichverteilt(
            sollBreite,
            zwischenabstand,
            standardBreite,
            minBreite,
            maxBreite
          );
          idealeBreite = elementBreite;
          if (restBreite > tolerance) {
            console.error(
              "calcElementAnzahlGleichverteilt: Es bleibt ein Rest von restBreite=",
              restBreite
            );
          }
          return anzahlElemente;
        },
        (i) => idealeBreite
      );
    } else if (strategie === "StandardLinks" || strategie === "StandardRechts") {
      const starteLinks = strategie === "StandardLinks";
      let elementBreiten;
      verteileElemente(
        starteLinks,
        (elementAbstand, sollBreite) => {
          const zwischenabstand = elementAbstand.zwischenabstand;
          let restBreite;
          ({ elementBreiten, restBreite } = calcElementAnzahlStandardMitMasselementen(
            sollBreite,
            zwischenabstand,
            standardBreite,
            minBreite,
            maxBreite
          ));
          elementBreiten.reverse();
          if (restBreite > tolerance) {
            console.error(
              "calcElementAnzahlStandardMitMasselementen: Es bleibt ein Rest von restBreite=",
              restBreite
            );
          }
          return elementBreiten.length;
        },
        // i==0 ist bei starteLinks==true ganz links und bei starteLinks==false ganz rechts
        (i) => elementBreiten[i]
      );
    } else if (strategie === "LeerraumLinks" || strategie === "LeerraumRechts") {
      const starteLinks = strategie === "LeerraumRechts";
      verteileElemente(
        starteLinks,
        (elementAbstand, sollBreite) => {
          const zwischenabstand = elementAbstand.zwischenabstand;
          const { anzahlGanzeElemente } = calcElementAnzahl(
            sollBreite,
            zwischenabstand,
            standardBreite
          );
          return anzahlGanzeElemente;
        },
        (i) => standardBreite
      );
    } else if (strategie === "KeinZaun") {
      this.parts = this.parts.filter((p) => p.isTor);
    } else {
      throw new Error("unknown strategie " + strategie);
    }

    // this.logit();
  }

  checkAndMaybeUpdateSnaps() {
    this.checkSnapCollision();
    if (this.ifCollisionsResnap()) {
      this.repositionParts(this.getPartsAndPfostenWithoutEmpties());
      this.updateEmpties();
      this.setNachbarn();
      if (this.ifStillCollisionsRemoveAbstände()) {
        this.repositionParts(this.getPartsAndPfostenWithoutEmpties());
        this.updateEmpties();
        this.setNachbarn();
      }
    }
  }

  repositionAndCheckSnaps() {
    const parts = this.getPartsAndPfosten().filter((x) => !x.automatischeBreite);
    this.repositionParts(parts);
    this.ifPartIsOverSegmentEndSetAllSnapsInOneDirection(parts);
    this.checkAndMaybeUpdateSnaps();
  }

  /**
   * Wird ein Element neben einem Doppeltor zu einem Einzeltor geändert und ist der Anschlag so,
   * dass es zu einem Fehler beim Dreifachtor kommt, wird er direkt umgedreht
   */
  korrigiereFalscheDreifachtore() {
    const dreifachTorParts = this.getParts().filter((p) => p.isDreifachtor);
    if (
      dreifachTorParts.length == 2 &&
      APP.arrangement.testDreifachtorFalscherAnschlag(dreifachTorParts)
    ) {
      const einzeltor = dreifachTorParts.find((tor) => tor.isEinzeltor);
      const neuerAnschlag = APP.metadata.Anschlag_Auswahlwert.find(
        (anschlagAuswahlwert) => anschlagAuswahlwert.Key != einzeltor.anschlagAuswahlwert.Key
      );
      if (neuerAnschlag) {
        einzeltor.anschlagAuswahlwert = neuerAnschlag;
      }
    }
  }

  /**
   * Wenn ein Einzeltor direkt neben einem Schiebetor eingefügt wird, setzt Öffnungsrichtung
   * und Anschlag des Einzeltors passend zur Schiebetor-Motorseite, damit die Kombination fehlerfrei ist.
   */
  korrigiereEinzeltorAnSchiebetor() {
    const elemente = this.getElemente() as ZElement[];
    const schiebetor = elemente.find((e) => e.isSchiebetor);
    if (!schiebetor) return;

    // Finde das Einzeltor, das direkt neben dem Schiebetor steht
    const einzeltor = (schiebetor.getSegmentNachbarn() as ZElement[]).find((e) => e?.isEinzeltor);
    if (!einzeltor) return;

    const einzeltorLinks = einzeltor.position.x < schiebetor.position.x;
    const isSchiebetorMotorseiteA = schiebetor.isSchiebetorMotorseiteA;

    // Öffnungsrichtung gleich der Schiebetor-Motorseite:
    // Motorseite A (außen) → Öffnung nach außen, Motorseite B (innen) → Öffnung nach innen
    const neueÖffnung = isSchiebetorMotorseiteA ? "Öffnung_nach_innen" : "Öffnung_nach_außen";
    einzeltor.öffnungAuswahlwert = APP.richMetadata.findAuswahlwert(neueÖffnung);

    // Anschlag so setzen, dass isAufhängungAnLinkerTorseite == einzeltorLinks (Validierungsregel)
    // isAufhängungAnLinkerTorseite = isAnschlagLinks XOR isÖffnungNachInnen
    // => isAnschlagLinks = einzeltorLinks XOR isÖffnungNachInnen
    const isÖffnungNachInnen = !isSchiebetorMotorseiteA;
    const neuerAnschlagLinks = einzeltorLinks !== isÖffnungNachInnen;
    einzeltor.anschlagAuswahlwert = APP.richMetadata.findAuswahlwert(
      neuerAnschlagLinks ? "Anschlag_rechts" : "Anschlag_links"
    );
  }

  ifPartIsOverSegmentEndSetAllSnapsInOneDirection(parts) {
    if (this.getÜberstände().length > 0) {
      const snap = Commons.maxFrequent(parts.map((e) => e.snap));
      parts.forEach((p) => (p.snap = snap));
      this.repositionParts(parts);
    }
  }

  logit() {
    this._debugTable = this.dumpParts(this.getPartsAndPfostenAndEckpfosten());
    console.table(this._debugTable);
  }

  diffit() {
    const oldTable = this._debugTable;
    const newTable = (this._debugTable = this.dumpParts(this.getPartsAndPfostenAndEckpfosten()));
    console.tablediff(newTable, oldTable, "id");
  }

  showXPosition(x: number, y = 0.1) {
    this.jaco3d.add(Debugs.line([v3(x, y, -1), v3(x, y, 1)], 0xff0000));
    STAGE.dirty();
  }

  dumpParts(parts) {
    const round = (x: number) => Maths.round(x, JSON_DECIMALS);
    const getX = (p: any, side: number) => (p == null ? NaN : this.getX(p, side));
    const gapX1 = (i: number) => round(getX(parts[i + 1], 0) - getX(parts[i], 1));
    const mapper = (part: any, i: number, parts: any[]) => ({
      id: part.id,
      x0: round(getX(part, 0)),
      x1: round(getX(part, 1)),
      // gkx: round(this.getGrundrissKanteX(this.getWorldPositionOfX(this.getX(part, 0.5)))),
      // wp: this.getWorldPositionOfX(this.getX(part, 0.5)) + "",
      gapX1: gapX1(i),
      gapLinks: round(parts[i]?.gapLinks ?? 0),
      gapRechts: round(parts[i]?.gapRechts ?? 0),
      type: getTypeNameOfPart(part),
      snap: part.snap,
      breite: round(part.breite),
      lichteWeite: round(part.lichteWeite ?? 0),
      automatischeBreite: part.automatischeBreite,
      ecke: part.ecke?.id,
      elemente: (part.elemente || []).map((e) => e.id).join(","),
      pfosten: (part.getPfosten?.() ?? []).map((e) => e?.id).join(","),
      meta: part.meta?.Key,
    });
    return parts.map(mapper);
  }

  updateCoordinateSystem() {
    this.direction.copy(Maths3.v2To3xz(this.segmentKante.grundrissKante.direction));
    this.normal.copy(Maths3.v2To3xz(this.segmentKante.grundrissKante.normal));
    this.flippedNormal = this.segmentKante.grundrissKante.flippedNormal;
    this.breite = this.ecken[0].position.distanceTo(this.ecken[1].position);
    this.position.copy(this.ecken[0].position);
    // Für 2d-Grundriss-Berechnungen benötigt
    this.angle = v2ToAngle(Maths3.v3To2xz(this.direction));
    // Das Quaternion für die 3d-Szene wird y-gespiegelt, damit die 2d-Grundriss-Orientierung mit
    // der 3D-Orientierung übereinstimmt.
    const angleQuaternion = v2ToAngle(Maths3.v3To2xz(this.direction).multiply(v2(1, -1)));
    this.quaternion.setFromAxisAngle(Maths3.vector.y, angleQuaternion);
  }

  createJaco3d() {
    const x = super.createJaco3d();
    if (APP.debugMode) {
      x.add(new OcAxisHelper(2));
      Debugs.line(this.ecken, 0xff0000, 5);
    }
    return x;
  }

  insertPfosten() {
    const insertPfosten = initInsertPfostenFunction(this);

    let partLinks = null;
    Commons.forEachFirstLast(this.getElemente(), (partRechts, index, isFirst, isLast) => {
      if (isFirst) {
        insertPfosten(null, partRechts);
      } else {
        const istZwischenabstand = this.getX(partRechts, 0) - this.getX(partLinks, 1);
        const sollZwischenabstand = getZwischenabstand(
          // weil hier nur Pfosten zwischen Elementen vorkommen,
          // müssen die Zusatzinfos wie isWinkelbereichU,isEckadapter,isSegmentende nicht übergeben werden
          toAbschnittsteil(partLinks),
          toAbschnittsteil(partRechts),
          this.arrangement
        );

        if (istZwischenabstand > sollZwischenabstand + pfostenbreiteCirca) {
          insertPfosten(partLinks, null);
          insertPfosten(null, partRechts);
        } else {
          insertPfosten(partLinks, partRechts);
        }
      }
      if (isLast) {
        insertPfosten(partRechts, null);
      }
      partLinks = partRechts;
    });
  }

  insertUMProfile() {
    // Achtung, auch ein Pfosten kann ein UMProfil sein - falls ein UMProfil statt eines Pfostens verwendet
    // wird (an Mauer). Diese Methode behandelt nur den Fall, wenn zwischen Element und Pfosten
    // ein zusätzliches UMProfil gesetzt wird.
    this.umprofile = [];
    this.getElemente().forEach((element) => {
      const { umprofilLinks, umprofilRechts, pfostenLinks, pfostenRechts } = element;
      element.umprofilLinks = null;
      element.umprofilRechts = null;
      const maybeInsertUMProfil = (pfosten, umprofil, umprofilKey, elementSide) => {
        if (pfosten?.meta == null) {
          return;
        }

        const dir = elementSide == 0 ? 1 : 0;
        const nachbarPfosten = pfosten.elemente[dir]?.getPfosten()?.[dir];
        const pfostenartAuswahlwert = nachbarPfosten?.isTorpfosten
          ? APP.arrangement.pfostenartAuswahlwert
          : nachbarPfosten?.pfostenartAuswahlwert;
        const isEckadapter =
          elementSide == 0 ? element.eckadapterLinks != null : element.eckadapterRechts != null;

        const angle = getPfostenWinkel(pfosten, element).angle;
        const isWinkelbereichU = isWinkelbereichUForPfosten(pfosten, angle);

        const isEinzeltorAnSchiebetorrahmen = element.isEinzeltor && pfosten.isSchiebetorpfosten;
        const umprofilParams = {
          pfostenfarbe: pfosten.pfostenfarbeAuswahlwert?.Objekt?.[0],
          pfostentyp: pfosten.meta.Pfostentyp,
          pfostenartAuswahlwert: pfostenartAuswahlwert ?? pfosten.pfostenartAuswahlwert,
          gesuchtePfostenHöhe: pfosten.gesuchtePfostenHöhe,
          aufbautyp: element.meta.Aufbautyp,
          toraufhängung: element.hasAufhängungAnPfosten(pfosten),
          torschloss: element.hasSchlossAnPfosten(pfosten),
          mauer: isEinzeltorAnSchiebetorrahmen,
          isElementhalter: element.isElementhalter,
          isEckadapter,
          isWinkelbereichU,
          ...(isEinzeltorAnSchiebetorrahmen && {
            anschlagprofilAuswahlwert: element.anschlagprofilAuswahlwert,
            anschlagprofilfarbeAuswahlwert: element.anschlagprofilfarbeAuswahlwert,
          }),
        };
        const umpfrofilMeta = APP.richMetadata.getUMProfilMeta(umprofilParams);
        if (umpfrofilMeta) {
          if (!umprofil) {
            umprofil = new UMProfil(this);
          }
          umprofil.parent = this;
          element[umprofilKey] = umprofil;
          this.umprofile.push(umprofil);
          umprofil.meta = umpfrofilMeta;
          umprofil.gesuchtePfostenHöhe = pfosten.gesuchtePfostenHöhe;
          // Positionierung erfolgt direkt am Pfosten
          const segment = element.segment;
          umprofil.update3d();
          const pfostenX = segment.getX(pfosten, dir);
          segment.setX(umprofil, 1 - dir, pfostenX);
        }
      };
      maybeInsertUMProfil(pfostenLinks, umprofilLinks, "umprofilLinks", 0);
      maybeInsertUMProfil(pfostenRechts, umprofilRechts, "umprofilRechts", 1);
    });
  }

  setNachbarn() {
    let partLinks = null;
    const parts = this.getPartsAndPfosten();
    Commons.forEachFirstLast(parts, (partRechts, index, isFirst, isLast) => {
      if (partLinks && !partLinks.isPfostenAnEcke) {
        partLinks.nachbarRechts = partRechts;
      }
      if (partRechts && !partRechts.isPfostenAnEcke) {
        partRechts.nachbarLinks = partLinks;
      }
      partLinks = partRechts;
      if (isLast) {
        partRechts.nachbarRechts = null;
      }
    });
  }

  // FIXME die Eckpfosten sollten in Arrangement gesammelt und an collectArtikel übergeben werden.
  getPfostenWithoutEckpfosten() {
    return this.pfosten.filter((p) => !p.isPfostenAnEcke);
  }

  removeCollidingPfosten() {
    let pfostenBefore = null;
    let changed = false;
    for (const pfosten of Array.from(this.getPfosten().filter((p) => !p.isUMProfil))) {
      if (pfostenBefore != null) {
        if (Math.abs(this.getX(pfosten, 0) - this.getX(pfostenBefore, 1)) < 0.01) {
          const pfostenToRemove = [pfosten, pfostenBefore].find((p) => !p.isPfostenAnEcke);
          if (pfostenToRemove) {
            const pfostenToKeep = [pfosten, pfostenBefore].find((p) => p !== pfostenToRemove);
            if (pfostenToKeep) {
              pfostenToRemove.elemente.forEach((element) => {
                if (element.pfostenLinks == pfostenToRemove) {
                  element.pfostenLinks = pfostenToKeep;
                  pfostenToKeep.elemente.push(element);
                }
                if (element.pfostenRechts == pfostenToRemove) {
                  element.pfostenRechts = pfostenToKeep;
                  pfostenToKeep.elemente.push(element);
                }
              });
            }
            // ist notwendig, weil update3d bereits aufgerufen wurde => Pfosten3d ist touchedByJaco
            pfostenToRemove.reset();
            Commons.remove(this.pfosten, pfostenToRemove);
            pfostenBefore = pfostenToKeep;
            changed = true;
            continue;
          }
        }
      }
      pfostenBefore = pfosten;
    }
    return changed;
  }

  calculateGaps(parts: any[]) {
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (part.isElement) {
        part.gapLinks = this._calculateGap(parts, i, -1);
        part.gapRechts = this._calculateGap(parts, i, +1);
        // if (part.eckadapterRechts && part.gapRechts != 0) debugger;
        // console.log("****", this._calculateGap(parts, i, +1));
      } else if (part.isEckadapter) {
        // Eckadapter gaps werden in Arrangement.updateEckadapter gesetzt
        continue;
      } else {
        part.gapLinks = 0;
        part.gapRechts = 0;
      }
    }
  }

  _calculateGap(parts: any[], index: number, direction: number) {
    const getZwischenteile = (parts, index, direction) => {
      const iterator = (function* () {
        for (let i = 0; true; i++) {
          const j = index + direction * i;
          if (j < 0 || j >= parts.length) break;
          yield parts[j];
        }
      })();

      const next = () => iterator.next().value;

      const result = {
        element: null,
        umprofil: null,
        eckadapter: null,
        pfosten: null,
      };

      // legt eine Reihenfolge fest, in der Teile aufeinander folgen müssen, um erkannt zu werden
      const checkers = [
        [(part) => part?.isElement, (part) => (result.element = part), true],
        [(part) => part?.isUMProfil, (part) => (result.umprofil = part), false],
        [(part) => part?.isEckadapter, (part) => (result.eckadapter = part), false],
        [(part) => part?.isPfosten, (part) => (result.pfosten = part), false],
      ] as [(part: any) => boolean, (part: any) => void, boolean][];

      let checkerIndex = 0;
      let part = next();
      while (part && checkerIndex < checkers.length) {
        const [condition, action, required] = checkers[checkerIndex];
        if (condition(part)) {
          action(part);
          part = next();
        } else if (required) {
          break;
        }
        checkerIndex++;
      }

      return result;
    };

    const { element, pfosten, umprofil, eckadapter } = getZwischenteile(parts, index, direction);
    let deltaabstandsmass = APP.richMetadata.getDeltaabstandsmass(
      element?.meta,
      pfosten?.meta,
      undefined,
      umprofil?.meta,
      eckadapter?.meta
    );

    return deltaabstandsmass;
  }

  repositionParts(parts: any[]) {
    this.calculateGaps(parts);
    // feststehende Teile, die über den Rand hinausgehen, rücken nach innen
    const segmentEndenAusgleich = this.ecken.map((ecke) => getSegmentEndenAusgleichForEcke(ecke));
    parts
      .filter(
        (part) => !part.isPfostenAnEcke && part.snap === 0 && (part.isEmpty || part.isElement)
      )
      .forEach((part) => {
        const x0 = this.getX(part.isElement ? part.pfostenLinks ?? part : part, 0);
        const x1 = this.getX(part.isElement ? part.pfostenRechts ?? part : part, 1);
        const min = 0 + segmentEndenAusgleich[0] + this.eckadapterDelta[0];
        const max = this.breite - segmentEndenAusgleich[1] - this.eckadapterDelta[1];
        const d0 = x0 < min ? min - x0 : 0;
        const d1 = max < x1 ? x1 - max : 0;
        const d = d0 - d1;
        if (Math.abs(d) > 0.001) {
          this.setX(part, 0, this.getX(part, 0) + d);
        }
      });
    // lasse Elemente nach links snappen
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if ((part.snap == -1 || part.automatischeBreite) && part.parent == this) {
        const pre = parts[i - 1];
        const gap = part.gapLinks + (pre?.gapRechts ?? 0);
        const x1 = pre ? this.getX(pre, 1) : 0;
        this.setX(part, 0, x1 + gap);
      }
    }
    // lasse Elemente nach rechts snappen
    for (let i = parts.length - 1; i >= 0; i--) {
      const part = parts[i];
      if ((part.snap == +1 || part.automatischeBreite) && part.parent == this) {
        const pre = parts[i + 1];
        const gap = (part.gapRechts ?? 0) + (pre?.gapLinks ?? 0);
        const x0 = pre ? this.getX(pre, 0) : this.breite;
        this.setX(part, 1, x0 - gap);
      }
    }
  }

  removeNonAutoEmptiesNextToEmpties() {
    const lengthBefore = this.parts.length;
    this.parts = this.parts.filter(
      (part) =>
        !(
          part.isEmpty &&
          !part.automatischeBreite &&
          (part.getPartAtRelativeIndex(-1)?.isEmpty || part.getPartAtRelativeIndex(+1)?.isEmpty)
        )
    );
    return this.parts.length != lengthBefore;
  }

  getÜberstände(): [number, number][] {
    const parts = this.getPartsAndPfosten();
    const min = _.min(parts.map((p) => this.getX(p, 0)));
    const max = _.max(parts.map((p) => this.getX(p, 1)));
    const intervalle = [];
    const epsilon = 0.009;
    const delta = epsilon + pfostenbreiteCirca / 2;
    if (min < 0 - delta) {
      intervalle.push([min, 0]);
    }
    if (max > this.breite + delta) {
      intervalle.push([this.breite, max]);
    }
    return intervalle;
  }

  updateEmpties() {
    const minimumEmptyBreite = 0.005;
    const emptyPool = this.lazyPool(this.empties, () => new Empty(this));
    segmentIntervallUnion(
      this.breite,
      this.getPartsAndPfosten()
        .filter((x) => !x.automatischeBreite)
        .map((part) =>
          part.isElement
            ? [this.getX(part.pfostenLinks ?? part, 0), this.getX(part.pfostenRechts ?? part, 1)]
            : [this.getX(part, 0), this.getX(part, 1)]
        ),
      true,
      minimumEmptyBreite
    ).forEach((i) => {
      const empty = emptyPool.next();
      empty.reset();
      empty.snap = empty.getSnapFromNachbarn();
      empty.position.x = i[0];
      empty.breite = i[1] - i[0];
      empty.isBreiteKonfigurierbar = this.getPartsAndPfostenWithoutEmpties().length > 0;
    });
    emptyPool.cleanup();
    this.empties.forEach((empty) => empty.update3d());
  }

  remove(part: any) {
    Commons.remove(this.parts, part);
    Commons.remove(this.empties, part);
  }

  getElemente(): ZElement[] {
    return this.parts.filter((e) => e.isElement).sort(this.compareX);
  }

  getTore(): ZElement[] {
    return this.parts.filter((e) => e.isTor).sort(this.compareX);
  }

  getParts(): any[] {
    return [].concat(this.parts).concat(this.empties).concat(this.platzhalter).sort(this.compareX);
  }

  getPartsAndPfosten(): any[] {
    return []
      .concat(this.getParts())
      .concat(this.getPfosten())
      .concat(this.umprofile)
      .concat(this.eckadapter)
      .sort(this.compareX);
  }

  getPartsAndPfostenAndEckpfosten(): any[] {
    return _.uniq(
      []
        .concat(this.getParts())
        .concat(this.getPfosten())
        .concat(this.ecken.map((e) => e.pfosten).filter((p) => p != null))
        .concat(this.umprofile)
        .concat(this.eckadapter)
    ).sort(this.compareX);
  }

  getPartsAndPfostenWithoutEmpties(): any[] {
    return this.getPartsAndPfosten().filter((p) => !p.automatischeBreite);
  }

  getPfosten(): Pfosten[] {
    return [].concat(this.pfosten).sort(this.compareX);
  }

  getEmpties(): Empty[] {
    return [].concat(this.empties).sort(this.compareX);
  }

  getUMProfile(): UMProfil[] {
    return this.umprofile.sort(this.compareX);
  }

  toJSON(): any {
    return {
      breite: Maths.round(this.breite, JSON_DECIMALS),
      partsAndPfosten: this.getPartsAndPfosten()
        .filter((x) => !x.automatischeBreite && !x.isPfostenAnEcke)
        .map((x) => x.toJSON()),
      verteilungsstrategieAuswahlwert: this.verteilungsstrategieAuswahlwert_?.Key,
    };
  }

  fromJSON(json: any) {
    this.breite = json?.breite;

    if (json != null && json.partsAndPfosten == null) {
      throw new Error("das alte JSON-Format ist nicht mehr lesbar");
    }

    const partsAndPfosten = json?.partsAndPfosten
      ?.filter((x) => x.type != "Mauer")
      ?.map((x) => createClass(x.type, this).fromJSON(x));
    partsAndPfosten.forEach((part, i) => {
      if (part.isElement) {
        if (partsAndPfosten[i - 1]?.isUMProfil) {
          part.umprofilLinks = partsAndPfosten[i - 1];
          if (partsAndPfosten[i - 2]?.isPfosten) {
            part.pfostenLinks = partsAndPfosten[i - 2];
          }
        } else if (partsAndPfosten[i - 1]?.isPfosten) {
          part.pfostenLinks = partsAndPfosten[i - 1];
        }
        if (partsAndPfosten[i + 1]?.isUMProfil) {
          part.umprofilRechts = partsAndPfosten[i + 1];
          if (partsAndPfosten[i + 2]?.isPfosten) {
            part.pfostenRechts = partsAndPfosten[i + 2];
          }
        } else if (partsAndPfosten[i + 1]?.isPfosten) {
          part.pfostenRechts = partsAndPfosten[i + 1];
        }
      }
    });

    const lists = _.groupBy(partsAndPfosten, (x) =>
      x.isUMProfil ? "umprofile" : x.isPfosten ? "pfosten" : "parts"
    );
    this.umprofile = lists.umprofile ?? [];
    this.pfosten = lists.pfosten ?? [];
    this.parts = lists.parts ?? [];
    this.verteilungsstrategieAuswahlwert = APP.richMetadata.findAuswahlwert(
      json?.verteilungsstrategieAuswahlwert
    );

    return this;
  }

  collectArtikel(iterator: StücklistenIterator) {
    []
      .concat(this.getParts())
      .concat(this.getPfostenWithoutEckpfosten())
      .concat(this.umprofile)
      .forEach((part) => part.collectArtikel(iterator));
  }

  get grundrissKante(): GrundrissKante {
    return this.segmentKante?.grundrissKante;
  }

  getRay(): Ray {
    if (this.ecken[0] == null) debugger;
    return new Ray(this.ecken[0].position, this.direction);
  }

  getXOfWorldPosition(position: Vector3): number {
    return Maths3.distanceToRay(this.getRay(), position);
  }

  getWorldPositionOfX(x: number): Vector3 {
    return this.getRay().at(x, v3());
  }

  /**
   * @param part
   * @param side 0 ist linke Kante, 1 ist rechte Kante, 0.5 ist Mitte des Parts
   * @returns {number} x-Koordinate bezogen auf das Segment
   */
  getX(part: any, side: number): number {
    const x =
      !part.isPfostenAnEcke && part.segment === this
        ? part.position.x
        : this.getXOfWorldPosition(part.getWorldPosition());
    // Bei Elementen und Leerräumen ist der Ursprung (ihrer Position) Links, bei Pfosten etc. dagegen Mittig
    // siehe auch RichMetadata._modifyModelData und GridPosition
    return part.isUrsprungLinks ? x + side * part.breite : x + (side - 0.5) * part.breite;
  }

  getMinMax() {
    const partsAndPfosten = this.getPartsAndPfosten();
    return {
      x0: this.getX(_.first(partsAndPfosten), 0),
      x1: this.getX(_.last(partsAndPfosten), 1),
    };
  }

  getMinMaxWorldPosition() {
    const minMax = this.getMinMax();
    return {
      x0: this.getWorldPositionOfX(minMax.x0),
      x1: this.getWorldPositionOfX(minMax.x1),
    };
  }

  findClosestSideToWorldPosition(worldPosition: Vector3) {
    return _.minBy([0, 1], (side) =>
      this.getWorldPositionOfX(side * this.breite).distanceTo(worldPosition)
    );
  }

  getGrundrissKanteX(position: Vector3) {
    const ray = this.getGrundrissKanteRay();
    return Maths3.distanceToRay(ray, position);
  }

  get grundrissEckpositionen() {
    return this.grundrissKante.ecken.map((e) => Maths3.v2To3xz(e.punkt));
  }

  getGrundrissKanteRay() {
    const [origin, target] = this.grundrissEckpositionen;
    const direction = target.clone().sub(origin).normalize();
    const ray = new Ray(origin.clone(), direction);
    return ray;
  }

  /**
   * @param part
   * @param side 0 ist linke Kante, 1 ist rechte Kante, 0.5 ist Mitte des Parts
   * @param x positioniert das Part so, dass die entsprechende Kante auf dieser x-Koordinate bezogen auf das Segment liegt
   */
  setX(part: any, side: number, x: number) {
    if (part.isPfostenAnEcke) {
      console.warn("ignore setX of an Eckpfosten");
      return;
    }
    if (part.parent !== this) {
      debugger;
      throw new Error("can only setX for what I own");
    }
    if (part.automatischeBreite) {
      if (side == 0) {
        const x1 = this.getX(part, 1);
        part.position.x = x;
        part.setBreite(Math.max(0, x1 - x));
      } else {
        part.setBreite(Math.max(0, x - part.position.x));
      }
    } else {
      part.position.x = part.isUrsprungLinks
        ? x - side * part.breite
        : x - (side - 0.5) * part.breite;
    }
  }

  getPartAtPositionX(positionX: number) {
    return this.getParts().find(
      (part) => this.getX(part, 0) <= positionX && positionX <= this.getX(part, 1)
    );
  }

  getPartOrPfostenAtPositionX(positionX: number) {
    return this.getPartsAndPfosten().find(
      (part) => this.getX(part, 0) <= positionX && positionX <= this.getX(part, 1)
    );
  }

  filterPartsBetweenPositionsX(x0: number, x1: number) {
    return this.getParts().filter((part) =>
      ClosedInterval.intersection(
        ClosedInterval.order([x0, x1]),
        ClosedInterval.order([this.getX(part, 0), this.getX(part, 1)])
      )
    );
  }

  checkSnapCollision() {
    const parts = this.getParts().filter((p) => !p.automatischeBreite);
    for (let i = 1; i < parts.length; i++) {
      const a = parts[i - 1];
      const b = parts[i];
      if (a.snap == +1 && b.snap == -1 && !(a.automatischeBreite || b.automatischeBreite)) {
        console.error("aufeinanderzeigende snaps");
        // debugger;
      }
    }
  }

  ifCollisionsResnap() {
    const parts = this.getParts().filter((p) => !p.automatischeBreite);
    const collisionsLeftIndex = this.findCollisionIndex(parts);
    let change = false;
    if (collisionsLeftIndex >= 0) {
      const countMinus = this.parts.filter((p) => p.snap < 0).length;
      const countPlus = this.parts.filter((p) => p.snap > 0).length;
      const newSnap = countMinus > countPlus ? -1 : +1;
      const otherSnaps = this.parts.filter((x) => x.snap != newSnap);
      otherSnaps.forEach((x) => (x.snap = newSnap));
      change = otherSnaps.length > 0;
    }
    return change;
  }

  ifStillCollisionsRemoveAbstände() {
    const parts = this.getParts();
    const collisionsLeftIndex = this.findCollisionIndex(parts);
    let change = false;
    if (collisionsLeftIndex >= 0) {
      const [snapDirection, dae] =
        [
          [+1, +1],
          [-1, 0],
        ]
          .map(([snapDirection, indexInc]: number[]): [number, any[]] => [
            snapDirection,
            this.findDurchgehendAnliegendeElementreihe(
              parts,
              snapDirection,
              collisionsLeftIndex + indexInc
            ),
          ])
          .find(([snapDirection, dae]) => dae.length > 0) ?? [];
      if (dae) {
        const toRemove = dae.filter((x) => x.isEmpty && !x.automatischeBreite);
        toRemove.forEach((x) => {
          x.uiRemove();
          change = true;
        });
      }
    }
    return change;
  }

  findCollisionIndex(parts: any[]) {
    const epsilon = 0.001;
    for (let i = 0; i < parts.length - 1; i++) {
      const a = parts[i];
      const b = parts[i + 1];
      const ax1 = a ? this.getX(a, 1) : 0;
      const bx0 = this.getX(b, 0);
      if (bx0 < ax1 + b.gapRechts + epsilon) {
        return i;
      }
    }
    return -1;
  }

  findDurchgehendAnliegendeElementreihe(
    parts: any[],
    snapDirection: number,
    index: number,
    nonAutoEmpties = true
  ): any[] {
    if (index === -1) {
      return [];
    }
    const dae = [];
    while (true) {
      index += snapDirection;
      const b = parts[index];
      if (b == null || (b.isEmpty && nonAutoEmpties === !b.automatischeBreite)) {
        break;
      }
      dae.push(b);
    }
    return dae;
  }

  getElementabschnitte(parts: any[], abschnittsbedingung: (part: any) => boolean) {
    let list = [];
    const listOfLists = [list];
    parts.forEach((part) => {
      if (part == null || !abschnittsbedingung(part)) {
        if (list.length > 0) {
          list = [];
          listOfLists.push(list);
        }
      } else {
        list.push(part);
      }
    });
    return listOfLists;
  }

  updateRuler() {
    const partsAndPfosten = this.getPartsAndPfosten();

    const abschnitte = APP.settings.Easy_DisplayZaunreiheRuler
      ? this.getElementabschnitte(partsAndPfosten, (part) => !part.isEmpty)
          .filter((list) => list.some((e) => e.isElement))
          .map((list) => {
            const x0 = this.getX(_.first(list), 0);
            const x1 = this.getX(_.last(list), 1);
            return { x0, x1, style: "Segment" as RulerStyle };
          })
      : [];

    const nichtGleichAbschnitt = (a) => !abschnitte.some((b) => a.x0 == b.x0 && a.x1 == b.x1);

    const elemente = APP.settings.Easy_DisplayElementeRuler
      ? this.getElementabschnitte(partsAndPfosten, (part) => !part.isEmpty && !part.isTor)
          .filter((list) => list.some((e) => e.isElement))
          .map((list) => {
            const first = _.first(list);
            const last = _.last(list);
            const x0 = this.getX(first, first?.isTorpfosten ? 1 : 0);
            const x1 = this.getX(last, last?.isTorpfosten ? 0 : 1);
            return { x0, x1, style: "Element" as RulerStyle };
          })
          .filter(nichtGleichAbschnitt)
      : [];

    const tore = APP.settings.Easy_DisplayTorRuler
      ? this.getElementabschnitte(partsAndPfosten, (part) => !part.isEmpty && part.isTor)
          .filter((list) => list.some((e) => e.isTor))
          .map((list) => {
            const x0 = this.getX(_.first(list).pfostenLinks, 0);
            const x1 = this.getX(_.last(list).pfostenRechts, 1);
            return { x0, x1, style: "Tor" as RulerStyle };
          })
          .filter(nichtGleichAbschnitt)
      : [];

    const schiebetorüberstände = APP.settings.Easy_DisplayTorRuler
      ? this.getTore()
          .filter((part) => part.isSchiebetor)
          .map((schiebetor) => {
            const { doppelPfostenAussen_x, torSpitzeWennOffen_x } =
              schiebetor.schiebetorExtrapunkte;
            const [x0, x1] = [doppelPfostenAussen_x, torSpitzeWennOffen_x].sort((a, b) => a - b);
            return { x0, x1, style: "Schiebetorüberstand" as RulerStyle };
          })
          .filter(nichtGleichAbschnitt)
      : [];

    const überstände = this.getÜberstände().map(([x0, x1]) => ({
      x0,
      x1,
      style: "Überstand" as RulerStyle,
    }));

    if (überstände.length > 0) {
      const mauern = APP.arrangement.aufbauten.filter((a) => a.isMauer() && !a.hatKollision);
      const ueberstandSegmente = überstände.map((u) => {
        const p0 = Maths3.v3To2xz(this.getWorldPositionOfX(u.x0));
        const p1 = Maths3.v3To2xz(this.getWorldPositionOfX(u.x1));
        return [p0, p1];
      });

      mauern.forEach((mauer) => {
        const poly = mauer.getEckenV2();
        for (const line of ueberstandSegmente) {
          if (
            Maths2.isPointInPolygon(line[0], poly) ||
            Maths2.isPointInPolygon(line[1], poly) ||
            Maths2.findLinePolygonIntersections(line, poly).length > 0
          ) {
            mauer.hatKollision = true;
            break;
          }
        }
      });
    }

    const ganzesSegment = nichtGleichAbschnitt(this.getMinMax())
      ? [{ ...this.getMinMax(), style: "GanzesSegment" as RulerStyle }]
      : [];

    const empties = APP.settings.Easy_DisplayLeerraumRuler
      ? this.getEmpties().map((e) => {
          return {
            x0: this.getX(e, 0),
            x1: this.getX(e, 1),
            style: "Empty" as RulerStyle,
          };
        })
      : [];

    const rulerDescriptors: { x0: number; x1: number; style: RulerStyle }[] = [
      ...abschnitte,
      ...elemente,
      ...tore,
      ...überstände,
      ...ganzesSegment,
      ...empties,
      ...schiebetorüberstände,
    ];

    // Z-Offsets werden gestapelt: sich in x überlappende Ruler bekommen unterschiedliche
    // Z-Ebenen (unabhängig vom Typ), damit sich die Maßlabels nicht überlagern. Der kürzere
    // Ruler liegt dabei näher (geringerer Z-Offset), bei gleicher Länge entscheidet der Typ.
    const versatzPerRuler = computeRulerVersatz(rulerDescriptors);

    const rulerPool = this.lazyPool(this.ruler, () => new AbschnittRuler(this));
    rulerDescriptors.forEach(({ x0, x1, style }, i) => {
      const ruler = rulerPool.next();
      ruler.x0 = x0;
      ruler.x1 = x1;
      ruler.style = style;
      ruler.versatz = versatzPerRuler[i] ?? RULER_BASE_VERSATZ;
      ruler.update3d();
    });
    rulerPool.cleanup();
  }

  filterElementeAusserhalbGrundstücksgrenze(epsilon = 0.1) {
    return this.getElemente().filter((p) => {
      const xs = [0, 1].map((side) => this.getX(p, side));
      return xs.some((x) => x < -epsilon) || xs.some((x) => x > this.breite + epsilon);
    });
  }

  setSnaps(snap: number) {
    this.getParts().forEach((part) => (part.snap = snap));
  }

  setSnapsToEdges() {
    const [a, b] = this.ecken.map((ecke) =>
      Commons.count(ecke.segmente, (segment: Segment) => segment.getElemente().length > 0)
    );
    const snap = a > b ? -1 : 1;
    this.setSnaps(snap);
  }

  istGleichmäßigeVerteilungMöglich(ausgangsPart: any) {
    const parts =
      this.getElementabschnitte(this.getParts(), (p) => p.isBreiteVariabel && !p.isTor).find(
        (list) => list.includes(ausgangsPart)
      ) ?? [];
    return parts.some((p) => p.isBreiteVariabel);
  }

  verteileBauseitsKürzbareElementeGleichmäßig(selectedParts: any[]) {
    const elementIstVariableAberKeinTor = (element) => element.isBreiteVariabel && !element.isTor;

    const minMax = (e: ZElement) => {
      const b = e.sizeRange.box;
      return [b.min.x, b.max.x];
    };

    const getAbschnitte = () =>
      this.getElementabschnitte(
        this.getPartsAndPfosten(),
        (p) =>
          p.isPfosten ||
          p.isUMProfil ||
          (p.isElement && elementIstVariableAberKeinTor(p)) ||
          p.automatischeBreite
      ).filter((abschnitt) => abschnitt.some(elementIstVariableAberKeinTor));

    const filterOwnParts = (liste) => this.getPartsAndPfosten().filter((p) => liste.includes(p));

    const getAbschnittZuSelectedListe = () => {
      const selectedZaunelemente = filterOwnParts(selectedParts)
        .filter(elementIstVariableAberKeinTor)
        .sort(this.compareX);
      let abschnitte = getAbschnitte();
      let abschnitt = null;
      let selectedListeImAbschnitt = null;
      let result = [];
      for (const element of selectedZaunelemente) {
        if (abschnitt == null || !abschnitt.includes(element)) {
          abschnitt = abschnitte.find((abschnitt) => abschnitt.includes(element));
          selectedListeImAbschnitt = [];
          result.push(selectedListeImAbschnitt);
        }
        selectedListeImAbschnitt.push(element);
      }
      return result;
    };

    for (const selectedListeImAbschnitt of getAbschnittZuSelectedListe()) {
      const bauseitskürzbareReihe = () => {
        const reihe =
          getAbschnitte().filter((list) => list.includes(selectedListeImAbschnitt[0]))[0] ?? [];

        if (selectedListeImAbschnitt.length > 1) {
          const selectedListePfosten = _.flatten(
            selectedListeImAbschnitt.map((e) => e.getPfosten())
          );
          return reihe.filter(
            (part) => selectedListeImAbschnitt.includes(part) || selectedListePfosten.includes(part)
          );
        }
        return reihe;
      };

      const getElemente = () => bauseitskürzbareReihe().filter((p) => p.isElement);
      const getBauseitsElemente = () => {
        const selectedListeImAbschnittOwn = filterOwnParts(selectedListeImAbschnitt);
        const elemente = (
          selectedListeImAbschnittOwn.length > 1
            ? selectedListeImAbschnittOwn
            : bauseitskürzbareReihe()
        ).filter(elementIstVariableAberKeinTor);
        if (elemente.length == 0) {
          return [];
        }
        const minMaxStr = (e) => {
          const mm = minMax(e);
          return mm[0] + "_" + mm[1];
        };
        const minMaxElement = Commons.maxFrequent(elemente, minMaxStr);
        const i = minMax(minMaxElement);
        // finde alle Elemente nach einer frei ausgedachten Heuristik
        const bauseitsElemente = elemente.filter((e) => {
          const j = minMax(e);
          const s = intersection(i as Interval, j as Interval);
          return ClosedInterval.size(s) / ClosedInterval.size(i as Interval) > 0.5;
        });
        return bauseitsElemente;
      };

      const getEmpties = () => bauseitskürzbareReihe().filter((p) => p.automatischeBreite);

      // alle Elemente gleich snappen
      const elementeToSnap = bauseitskürzbareReihe().filter((e) => !e.isPfosten);
      const snap = Commons.maxFrequent(elementeToSnap.map((e) => e.snap));
      elementeToSnap.forEach((e) => (e.snap = snap));

      this.arrangement.update3d();
      // leider muss man neuerdings update3d immer zur Sicherheit zweimal aufrufen...
      this.arrangement.update3d();

      const reihe = bauseitskürzbareReihe();
      if (reihe.length == 0) {
        console.log("keine Verteilung möglich, da es kein Element im Abschnitt gibt");
        return;
      }
      // console.log('bauseitskürzbareReihe',bauseitskürzbareReihe());

      // this.logit();

      const gesamtbreite = (list) => {
        if (list.length == 0) {
          return 0;
        }
        const x0: number = _.min(list.map((e) => this.getX(e, 0)));
        const x1: number = _.max(list.map((e) => this.getX(e, 1)));
        return x1 - x0;
      };

      const gesamtbreite_vorher = gesamtbreite(getElemente());

      // const nichtBauseitsElementabschnitte = this.getElementabschnitte(
      //   bauseitskürzbareReihe(),
      //   (p) => p.isPfosten || p.isUMProfil || (p.isElement && !elementIstVariableAberKeinTor(p))
      // ).filter((abschnitt) =>
      //   abschnitt.some((p) => p.isElement && !elementIstVariableAberKeinTor(p))
      // );
      // const sumNichtBauseitsElementabschnitte = _.sum(
      //   nichtBauseitsElementabschnitte.map((abschnitt) => {
      //     // Pfosten an den Enden der Abschnitte aus der Summe herausnehmen
      //     let min = 0;
      //     let max = abschnitt.length;
      //     while (min < max && (abschnitt[min].isPfosten || abschnitt[min].isUMProfil)) {
      //       min++;
      //     }
      //     while (min < max && (abschnitt[max - 1].isPfosten || abschnitt[max - 1].isPfosten)) {
      //       max--;
      //     }
      //     return gesamtbreite(abschnitt.slice(min, max));
      //   })
      // );
      //

      const bauseitsElemente = getBauseitsElemente();
      const [minBreite, maxBreite] =
        bauseitsElemente
          .map(minMax)
          .reduce(
            (a, b) =>
              a == null || b == null
                ? null
                : intersection(a as any as Interval, b as any as Interval),
            [0.1, 10]
          ) ?? [];

      //statt des Gaps werden hier die Abstände der Elemente direkt verwendet, damit Gaps, Pfosten und UMProfile gleichermaßen berücksichtigt werden
      const elementAbstände = bauseitsElemente.flatMap((e) => {
        const n = e.getSegmentNachbarn();
        const distance = (l, r) =>
          l?.isElement && r?.isElement ? l.segment.getX(r, 0) - l.segment.getX(l, 1) : 0;
        return [distance(n[0], e), distance(e, n[1])];
      });
      const sollElementAbstand = Commons.maxFrequent(elementAbstände);
      const medianBreite = Commons.median(bauseitsElemente.map((e) => e.breite));
      const medianElement = bauseitsElemente.find((e) => e.breite == medianBreite);

      // reihe enthält Elemente, aber auch Leeräume, weil diese aufgefüllt werden sollen.
      // reihe muss also eventuell an Mauern grenzen, oder mit einem Eckpfosten enden, oder an einem normalen Pfosten, oder leer sein
      // Für die Enden der Reihe muss das Delta zwischen ist- und soll bezüglich (Pfosten/UMProfil/Deltaabstandsmaß) ermittelt werden

      const firstInReihe = _.first(reihe);
      const lastInReihe = _.last(reihe);
      const pfostenOrUMProfil = (p: any) => p?.isPfosten || p?.isUMProfil;
      const nextNotPfosten = (start: any, dir: number) =>
        pfostenOrUMProfil(reihe[start]) ? nextNotPfosten(start + dir, dir) : reihe[start];

      const notPfostenFirst = nextNotPfosten(0, +1);
      const notPfostenLast = nextNotPfosten(reihe.length - 1, -1);

      // Bei firstInReihe hat man eventuell die Eckpfosten erwischt, und deren X-Außengrenzen stehen beim Rechteck eine halbe Pfostenbreite über den Segmentenden,
      // aber deltaLinks/deltaRechts funktionieren nur für Elemente oder Empties innerhalb des Segmentes, daher muss man die x-Werte auf das Segment beschränken eingrenzen
      const clampX = (x) => Maths.cutBetween(0, x, this.breite);
      const x0 = clampX(notPfostenFirst ? this.getX(notPfostenFirst, 0) : 0);
      const x1 = clampX(notPfostenLast ? this.getX(notPfostenLast, 1) : this.breite);
      const breiteAussengrenze = x1 - x0;

      const deltaLinks = getIstSollZwischenabstandDelta(notPfostenFirst, medianElement, LINKS);
      const deltaRechts = getIstSollZwischenabstandDelta(notPfostenLast, medianElement, RECHTS);

      // prettier-ignore
      const sollBreite =
          breiteAussengrenze
          + deltaLinks
          + deltaRechts;
      // - sumNichtBauseitsElementabschnitte

      const anzahl = (elementBreite: number) =>
        calcElementAnzahl(sollBreite, sollElementAbstand, elementBreite).anzahlElementeRational;

      const elementBreiteBeiAnzahl = (anzahl: number) =>
        calcElementBreite(sollBreite, sollElementAbstand, anzahl);

      const minAnzahl = anzahl(maxBreite);
      const maxAnzahl = anzahl(minBreite);
      const möglicheElementAnzahlen = _.range(Math.ceil(minAnzahl), Math.floor(maxAnzahl) + 1);
      const möglicheBreiteBeiAnzahl = möglicheElementAnzahlen.map(elementBreiteBeiAnzahl);

      if (möglicheElementAnzahlen.length == 0) {
        console.log("keine Verteilung!", {
          gesamtbreite_vorher,
          // firstInReihe,
          // lastInReihe,
          notPfostenFirst,
          notPfostenLast,
          deltaLinks,
          deltaRechts,
          // segmentEndenAusgleichLinks,
          // segmentEndenAusgleichRechts,
          x0,
          x1,
          breiteAussengrenze,
          sollBreite,
          minAnzahl,
          maxAnzahl,
          möglicheElementAnzahlen,
          möglicheBreiteBeiAnzahl,
          medianBreite,
          medianElement,
          reihe,
          bauseitsElemente,
          // sumNichtBauseitsElementabschnitte,
          sollElementAbstand,
          minBreite,
          maxBreite,
        });
        Dialogs.showToast(I18N("Leider kann ich keine gleichmäßige Verteilung finden"));
        return;
      }
      const idealeElementAnzahl = _.minBy(möglicheElementAnzahlen, (anzahl) =>
        Math.abs(medianBreite - elementBreiteBeiAnzahl(anzahl))
      );
      const idealeElementBreite = elementBreiteBeiAnzahl(idealeElementAnzahl);

      // const sollBreiteCheck =
      //   idealeElementAnzahl * idealeElementBreite + (idealeElementAnzahl - 1) * sollElementAbstand;

      // console.log(
      //   "sollwerte",
      //   Debugs.roundValues({
      //     selectedListeImAbschnitt,
      //     gesamtbreite_vorher,
      //     // firstInReihe,
      //     // lastInReihe,
      //     notPfostenFirst,
      //     notPfostenLast,
      //     deltaLinks,
      //     deltaRechts,
      //     // segmentEndenAusgleichLinks,
      //     // segmentEndenAusgleichRechts,
      //     x0,
      //     x1,
      //     breiteAussengrenze,
      //     sollBreite,
      //     sollBreiteCheck,
      //     minAnzahl,
      //     maxAnzahl,
      //     möglicheElementAnzahlen,
      //     möglicheBreiteBeiAnzahl,
      //     medianBreite,
      //     medianElement,
      //     idealeElementAnzahl,
      //     idealeElementBreite,
      //     reihe,
      //     bauseitsElemente,
      //     // sumNichtBauseitsElementabschnitte,
      //     sollElementAbstand,
      //     minBreite,
      //     maxBreite,
      //   })
      // );

      bauseitsElemente.forEach((element) => element.setBreite(idealeElementBreite));
      this.repositionAndCheckSnaps();
      this.arrangement.update3d();
      // leider muss man neuerdings update3d immer zur Sicherheit zweimal aufrufen...
      this.arrangement.update3d();

      let maxIterations1 = getBauseitsElemente().length - idealeElementAnzahl;
      while (maxIterations1-- > 0) {
        const first = _.first(getBauseitsElemente());
        if (this.parts.includes(first)) {
          this.remove(first);
        } else {
          debugger;
        }
      }

      this.arrangement.update3d();
      // leider muss man neuerdings update3d immer zur Sicherheit zweimal aufrufen...
      this.arrangement.update3d();

      let maxIterations2 = idealeElementAnzahl - getBauseitsElemente().length;
      while (maxIterations2-- > 0) {
        const empty = _.maxBy(
          getEmpties().filter((e) => e.automatischeBreite),
          (e) => e.breite
        );
        if (empty == null) {
          break;
        }
        // const angrenzendeEckpfostenBreite = _.sum(empty.getPfosten().filter(p=>p.isPfostenAnEcke).map(p=>p.breite));
        // if (empty==null || empty.breite+mostPGB+0.01+angrenzendeEckpfostenBreite<idealeElementBreite) {
        //     const diff = empty.breite+mostPGB+0.01+angrenzendeEckpfostenBreite-idealeElementBreite;
        //     console.log('.. but empty is too small',empty,{emptybreite:empty.breite,mostPGB,angrenzendeEckpfostenBreite,idealeElementBreite,diff,pfosten: empty.getPfosten()});
        //     break;
        // }
        const element = medianElement.clone();
        element.parent = this;
        element.setBreite(idealeElementBreite);
        this.setX(element, 0.5, this.getX(empty, 0.5));
        this.addPart(element);
        this.repositionAndCheckSnaps();

        // fixme update3d raus aus der Schleife
        this.arrangement.update3d();
        // leider muss man neuerdings update3d immer zur Sicherheit zweimal aufrufen...
        this.arrangement.update3d();
      }

      // Es kann immer noch sein, dass die Gesamtlänge nicht stimmt - das liegt wahrscheinlich an nicht einheitlichen Pfosten / UMProfilen / Gaps
      // weil das nicht im Vorfeld mit einer einfachen Durchschnittsbetrachtung wie oben zu berechnen ist, wird hier nachgemessen und das fehlende Delta nachverteilt
      // let gesamtbreite_nachher = gesamtbreite(
      //   bauseitskürzbareReihe().filter((p) => !(p.isEmpty && p.automatischeBreite))
      // );
      // const missingDelta = breiteAussengrenze - gesamtbreite_nachher;
      // const elementeZumNachjustieren = getBauseitsElemente();
      // if (Math.abs(missingDelta) > 0.001 && elementeZumNachjustieren.length > 0) {
      //   const deltaPerElement = missingDelta / elementeZumNachjustieren.length;
      //   const breitenVorher = elementeZumNachjustieren.map((e) => e.breite);
      //   elementeZumNachjustieren.forEach((element) => (element.breite += deltaPerElement));
      //   const breitenNachher = elementeZumNachjustieren.map((e) => e.breite);
      //   this.arrangement.update3d();
      //   // leider muss man neuerdings update3d immer zur Sicherheit zweimal aufrufen...
      //   this.arrangement.update3d();
      //   const breitenNachher2 = elementeZumNachjustieren.map((e) => e.breite);
      //   console.log(
      //     "nach nachjustieren",
      //     Debugs.roundValues({
      //       missingDelta,
      //       deltaPerElement,
      //       breiteAussengrenze,
      //       gesamtbreite_nachher,
      //       breitenVorher,
      //       breitenNachher,
      //       breitenNachher2,
      //     })
      //   );
      // }

      const gesamtbreite_nachher = gesamtbreite(bauseitskürzbareReihe());

      //   console.log(
      //     "endergebnis",
      //     Debugs.roundValues({
      //       gesamtbreite_vorher,
      //       gesamtbreite_nachher,
      //       firstInReihe,
      //       lastInReihe,
      //       notPfostenFirst,
      //       notPfostenLast,
      //       deltaLinks,
      //       deltaRechts,
      //       // segmentEndenAusgleichLinks,
      //       // segmentEndenAusgleichRechts,
      //       x0,
      //       x1,
      //       breiteAussengrenze,
      //       sollBreite,
      //       minAnzahl,
      //       maxAnzahl,
      //       möglicheElementAnzahlen,
      //       möglicheBreiteBeiAnzahl,
      //       medianBreite,
      //       medianElement,
      //       idealeElementAnzahl,
      //       idealeElementBreite,
      //       reihe,
      //       bauseitsElemente,
      //       // sumNichtBauseitsElementabschnitte,
      //       sollElementAbstand,
      //       minBreite,
      //       maxBreite,
      //     })
      //   );
    }

    // this.logit();

    APP.arrangement.setSelectedObjects(null);
  }

  addElemente(
    xStart: number,
    element: ZElement,
    deltaAbstand: number,
    anzahl: number,
    snap: number
  ) {
    const added = [];
    const side = snap == -1 ? 0 : 1;
    let currentX = xStart;
    for (let i = 0; i < Math.floor(anzahl); i++) {
      const clone = element.clone();
      clone.parent = this;
      clone.snap = snap;
      this.setX(clone, side, currentX);
      currentX -= snap * (clone.breite + deltaAbstand);
      this.addPart(clone);
      added.push(clone);
    }
    return { added, currentX };
  }

  distanceBetween(partA, partB) {
    if (partA == null || partB == null) {
      return Number.POSITIVE_INFINITY;
    }
    return (
      _.min(
        [
          this.getX(partA, 0) - this.getX(partB, 1),
          this.getX(partB, 0) - this.getX(partA, 1),
        ].filter((x) => x > 0)
      ) ?? Number.POSITIVE_INFINITY
    );
  }

  addPart(part) {
    if (!this.parts.includes(part)) {
      this.parts.push(part);
    }
    part.parent = this;
    return part;
  }

  setVerteilungsmodusIndividuell() {
    const individuell = APP.richMetadata.findAuswahlwert("Individuell");
    if (individuell !== this.verteilungsstrategieAuswahlwert) {
      this.verteilungsstrategieAuswahlwert = individuell;
      Dialogs.showToast(
        I18N("Die Verteilung eines Segmentes wurde auf %s geändert", individuell.Name)
      );
    }
  }

  maybeFitToreZwischenMauern() {
    const epsilon = 0.06;
    const zwischenMauern = (part) => part.anMauerLinksRechtsOderBeide === "AnMauer_beide";
    const toreAnMauern = this.getElemente().filter((e) => e.isTor && e.anMauer);
    toreAnMauern.forEach((tor) => {
      tor.eAntriebAuswahlwert = APP.richMetadata.findAuswahlwert("E_Antrieb_ohne");
      if (zwischenMauern(tor)) {
        const lückeZwischenTorUndMauer = tor.segment.breite - tor.lichteWeite;
        if (lückeZwischenTorUndMauer < epsilon) {
          tor.setKonfigurierbareBreite(tor.breite + lückeZwischenTorUndMauer);
        }
      }
    });
  }

  getAngleFromEcke(ecke: Ecke) {
    const eckeIndex = this.ecken.findIndex((x: Ecke) => x === ecke);
    return eckeIndex == 0 ? this.angle : 2 * Math.PI - this.angle;
  }
}
